import { useState, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Edit2, Power, PowerOff, Trash2, BookOpen, ChevronRight, FolderOpen, X, Check } from "lucide-react";

function ArchiveForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.archive_name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Arşiv adı zorunlu");
    setSaving(true);
    try { await onSave({ archive_name: name.trim(), description: desc }); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-600">Arşiv Adı *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Matematik Soru Havuzu"
            data-testid="archive-name-input" className="h-8 text-xs border-slate-200" required />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-600">Açıklama</Label>
          <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Kısa açıklama"
            data-testid="archive-desc-input" className="h-8 text-xs border-slate-200" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} data-testid="save-archive-btn"
          className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white gap-1">
          <Check size={11} /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="h-7 text-xs border-slate-200">
          <X size={11} className="mr-1" /> İptal
        </Button>
      </div>
    </form>
  );
}

export default function ArchiveList() {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/archives?include_inactive=${includeInactive}`);
      setArchives(res.data.archives);
    } catch { toast.error("Arşivler yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchArchives(); }, [includeInactive]);

  const handleCreate = async (data) => {
    try {
      await api.post("/archives", data);
      toast.success("Arşiv oluşturuldu");
      setShowCreate(false);
      fetchArchives();
    } catch (e) { toast.error(e.response?.data?.detail || "Oluşturma başarısız"); }
  };

  const handleUpdate = async (id, data) => {
    try {
      await api.put(`/archives/${id}`, data);
      toast.success("Arşiv güncellendi");
      setEditId(null);
      fetchArchives();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncelleme başarısız"); }
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`"${name}" arşivini pasife almak istiyor musunuz?\nBu arşivdeki tüm aktif sorular da pasif olacak.`)) return;
    try {
      const res = await api.delete(`/archives/${id}`);
      const count = res.data.affected_questions ?? 0;
      toast.success(`Arşiv pasife alındı. ${count > 0 ? `${count} soru da pasif edildi.` : "Etkilenen soru yok."}`);
      fetchArchives();
    } catch { toast.error("İşlem başarısız"); }
  };

  const handleActivate = async (id) => {
    try {
      const res = await api.post(`/archives/${id}/activate`);
      const count = res.data.affected_questions ?? 0;
      toast.success(`Arşiv aktife alındı. ${count > 0 ? `${count} soru da aktif edildi.` : "Etkilenen soru yok."}`);
      fetchArchives();
    } catch { toast.error("İşlem başarısız"); }
  };

  const handleViewQuestions = async (id) => {
    if (viewId === id) { setViewId(null); setViewData(null); return; }
    setViewId(id);
    setLoadingView(true);
    try {
      const res = await api.get(`/archives/${id}/questions`);
      setViewData(res.data);
    } catch { toast.error("Sorular yüklenemedi"); }
    finally { setLoadingView(false); }
  };

  return (
    <div className="p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Arşiv Yönetimi</h1>
          <p className="text-sm text-slate-500 mt-0.5">{archives.length} arşiv koleksiyonu</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="rounded" />
            Pasifler dahil
          </label>
          <Button onClick={() => setShowCreate(true)} data-testid="create-archive-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-8 gap-2">
            <Plus size={14} /> Yeni Arşiv
          </Button>
        </div>
      </div>

      {showCreate && (
        <div className="mb-5">
          <ArchiveForm onSave={handleCreate} onCancel={() => setShowCreate(false)} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="archives-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Arşiv Adı</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Açıklama</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-20 text-center">Soru</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Durum</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-36">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i} className="border-b border-slate-50">
                  {[...Array(5)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}
                </tr>
              ))
            ) : archives.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center">
                <FolderOpen size={28} className="text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Arşiv bulunamadı</p>
              </td></tr>
            ) : archives.map((a, i) => (
              <>
                <tr key={a.id} className={`hover:bg-slate-50 transition-colors ${i < archives.length - 1 || viewId === a.id ? "border-b border-slate-100" : ""}`}
                  data-testid={`archive-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-800">{a.archive_name}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs truncate max-w-xs">{a.description || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-semibold text-slate-700">{a.question_count ?? 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${
                      a.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {a.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleViewQuestions(a.id)} data-testid={`view-archive-${a.id}`}
                        className={`p-1.5 rounded transition-colors ${viewId === a.id ? "text-blue-600 bg-blue-50" : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"}`}
                        title="Soruları Gör">
                        <BookOpen size={13} />
                      </button>
                      <button onClick={() => setEditId(editId === a.id ? null : a.id)} data-testid={`edit-archive-${a.id}`}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors" title="Düzenle">
                        <Edit2 size={13} />
                      </button>
                      {a.is_active ? (
                        <button onClick={() => handleDeactivate(a.id, a.archive_name)} data-testid={`deactivate-archive-${a.id}`}
                          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors" title="Pasife Al">
                          <PowerOff size={13} />
                        </button>
                      ) : (
                        <button onClick={() => handleActivate(a.id)} data-testid={`activate-archive-${a.id}`}
                          className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="Aktife Al">
                          <Power size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Edit form row */}
                {editId === a.id && (
                  <tr key={`edit-${a.id}`} className="border-b border-slate-100">
                    <td colSpan={5} className="px-4 py-3">
                      <ArchiveForm initial={a} onSave={(data) => handleUpdate(a.id, data)} onCancel={() => setEditId(null)} />
                    </td>
                  </tr>
                )}

                {/* Questions row */}
                {viewId === a.id && (
                  <tr key={`view-${a.id}`} className="border-b border-slate-100 bg-slate-50">
                    <td colSpan={5} className="px-4 py-3">
                      {loadingView ? (
                        <p className="text-xs text-slate-400 py-2">Yükleniyor...</p>
                      ) : !viewData?.questions?.length ? (
                        <p className="text-xs text-slate-400 py-2">Bu arşivde soru bulunamadı</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="text-left py-1.5 font-medium w-24">Kod</th>
                                <th className="text-left py-1.5 font-medium">Soru</th>
                                <th className="text-left py-1.5 font-medium w-20">Durum</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {viewData.questions.map((q) => (
                                <tr key={q.question_code}>
                                  <td className="py-1.5 font-mono text-blue-600">{q.question_code}</td>
                                  <td className="py-1.5 text-slate-600 truncate max-w-xs">{q.question_text}</td>
                                  <td className="py-1.5">
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${q.status === "active" ? "text-green-600" : "text-slate-400"}`}>
                                      {q.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
